import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { Button } from "../ui/button";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Heading2, Heading3, Undo, Redo, Pilcrow,
} from "lucide-react";

const Tool = ({ active, onClick, title, children, testid }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className={`h-8 w-8 flex items-center justify-center rounded text-slate-600 hover:bg-slate-200 transition-colors ${active ? "bg-slate-900 text-white hover:bg-slate-900" : ""}`}
  >
    {children}
  </button>
);

const RichTextEditor = ({ value, onChange, variables = [], testid }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none min-h-[280px] focus:outline-none px-4 py-3 text-sm",
        "data-testid": testid ? `${testid}-area` : "rte-area",
      },
    },
  });

  // Sincronizează conținutul când se schimbă din exterior (load / reset la implicit)
  useEffect(() => {
    if (editor && !editor.isFocused && (value || "") !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const insertVariable = (key) => {
    editor.chain().focus().insertContent(`{{${key}}}`).run();
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 border-b bg-slate-50 px-2 py-1.5">
        <Tool title="Titlu mare" active={editor.isActive("heading", { level: 2 })} testid={`${testid}-h2`}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></Tool>
        <Tool title="Subtitlu" active={editor.isActive("heading", { level: 3 })} testid={`${testid}-h3`}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></Tool>
        <Tool title="Paragraf normal" active={editor.isActive("paragraph")}
          onClick={() => editor.chain().focus().setParagraph().run()}><Pilcrow className="h-4 w-4" /></Tool>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Tool title="Îngroșat" active={editor.isActive("bold")} testid={`${testid}-bold`}
          onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></Tool>
        <Tool title="Înclinat" active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></Tool>
        <Tool title="Subliniat" active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="h-4 w-4" /></Tool>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Tool title="Listă cu buline" active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></Tool>
        <Tool title="Listă numerotată" active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></Tool>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Tool title="Anulează" onClick={() => editor.chain().focus().undo().run()}><Undo className="h-4 w-4" /></Tool>
        <Tool title="Refă" onClick={() => editor.chain().focus().redo().run()}><Redo className="h-4 w-4" /></Tool>

        {variables.length > 0 && (
          <div className="ml-auto flex items-center gap-1">
            <span className="text-[11px] text-slate-400">Inserează variabilă:</span>
            <select
              className="h-8 border rounded px-2 text-xs bg-white"
              value=""
              onChange={(e) => { if (e.target.value) { insertVariable(e.target.value); e.target.value = ""; } }}
              data-testid={`${testid}-insert-var`}
            >
              <option value="">{`{{ … }}`}</option>
              {variables.map((v) => (
                <option key={v.key} value={v.key} title={v.desc}>{`{{${v.key}}}`}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
